import { useMemo,useReducer } from 'react';
import { LessonStep } from '../types';

// Типы для состояния урока
interface LessonState {
  currentStepIndex: number;
  steps: LessonStep[];
  scores: number[];
  isCompleted: boolean;
  isLoading: boolean;
  error: string | null;
  selectedLetter: string | null;
  letterProgress: Map<string, 'not_started' | 'in_progress' | 'completed'>;
}

// Типы действий
type LessonAction =
  | { type: 'SET_LETTER'; payload: string }
  | { type: 'SET_STEPS'; payload: LessonStep[] }
  | { type: 'NEXT_STEP'; payload?: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'COMPLETE_LESSON' }
  | { type: 'RESET_LESSON' }
  | { type:'REPLACE_LETTER_PROGRESS';payload:LessonState['letterProgress'] }
  | { type: 'UPDATE_LETTER_PROGRESS'; payload: { letter: string; progress: 'not_started' | 'in_progress' | 'completed' } };

// Начальное состояние
const initialState: LessonState = {
  currentStepIndex: 0,
  steps: [],
  scores: [],
  isCompleted: false,
  isLoading: false,
  error: null,
  selectedLetter: null,
  letterProgress: new Map(),
};

// Редьюсер для управления состоянием урока
function lessonReducer(state: LessonState, action: LessonAction): LessonState {
  switch (action.type) {
    case 'SET_LETTER':
      return {
        ...state,
        selectedLetter: action.payload,
        currentStepIndex: 0,
        scores: [],
        isCompleted: false,
        error: null,
      };

    case 'SET_STEPS':
      return {
        ...state,
        steps: action.payload,
        currentStepIndex: 0,
        scores: [],
        isCompleted: false,
        isLoading: false,
        error: null,
      };

    case 'NEXT_STEP':
      const newScores = [...state.scores];
      if (action.payload !== undefined) {
        newScores[state.currentStepIndex] = action.payload;
      }

      const nextIndex = state.currentStepIndex + 1;
      const isCompleted = nextIndex >= state.steps.length;

      return {
        ...state,
        currentStepIndex: nextIndex,
        scores: newScores,
        isCompleted,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'COMPLETE_LESSON':
      return {
        ...state,
        isCompleted: true,
      };

    case 'REPLACE_LETTER_PROGRESS': return {...state,letterProgress:new Map(action.payload)};
    case 'RESET_LESSON':
      return {
        ...state,
        currentStepIndex: 0,
        steps:[],selectedLetter:null,isLoading:false,
        scores: [],
        isCompleted: false,
        error: null,
      };

    case 'UPDATE_LETTER_PROGRESS':
      const newProgress = new Map(state.letterProgress);
      newProgress.set(action.payload.letter, action.payload.progress);
      return {
        ...state,
        letterProgress: newProgress,
      };

    default:
      return state;
  }
}

// Хук для управления состоянием урока
export const useLessonState = () => {
  const [state, dispatch] = useReducer(lessonReducer, initialState);

  // Мемоизированные действия
  const actions = useMemo(() => ({
    setLetter: (letter: string) => dispatch({ type: 'SET_LETTER', payload: letter }),
    setSteps: (steps: LessonStep[]) => dispatch({ type: 'SET_STEPS', payload: steps }),
    nextStep: (score?: number) => dispatch({ type: 'NEXT_STEP', payload: score }),
    setLoading: (loading: boolean) => dispatch({ type: 'SET_LOADING', payload: loading }),
    setError: (error: string | null) => dispatch({ type: 'SET_ERROR', payload: error }),
    completeLesson: () => dispatch({ type: 'COMPLETE_LESSON' }),
    resetLesson: () => dispatch({ type: 'RESET_LESSON' }),
    replaceLetterProgress:(progress:LessonState['letterProgress'])=>dispatch({type:'REPLACE_LETTER_PROGRESS',payload:progress}),
    updateLetterProgress: (letter: string, progress: 'not_started' | 'in_progress' | 'completed') =>
      dispatch({ type: 'UPDATE_LETTER_PROGRESS', payload: { letter, progress } }),
  }), []);

  // Мемоизированные вычисляемые значения
  const computed = useMemo(() => ({
    currentStep: state.steps[state.currentStepIndex] || null,
    isLastStep: state.currentStepIndex >= state.steps.length - 1,
    progress: state.steps.length > 0 ? (state.currentStepIndex / state.steps.length) * 100 : 0,
    averageScore: state.scores.length > 0
      ? state.scores.reduce((sum, score) => sum + score, 0) / state.scores.length
      : 0,
    totalScore: state.scores.reduce((sum, score) => sum + score, 0),
  }), [state.currentStepIndex,state.steps,state.scores]);

  return {
    state,
    actions,
    computed,
  };
};

export type { LessonState,LessonAction };
